import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Edit2, Trash2, AlertTriangle } from "lucide-react";
import { Pill } from "@/components/badges";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ServiceCategoriesPage() {
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({}); // {cat_id: {lead_count, opp_count, total}}
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const { data } = await api.get("/service-categories");
    setItems(data);
    // load usage in parallel
    const map = {};
    await Promise.all(
      data.map(async (c) => {
        try {
          const r = await api.get(`/service-categories/${c.id}/usage`);
          map[c.id] = r.data;
        } catch {
          map[c.id] = { lead_count: 0, opp_count: 0, total: 0 };
        }
      })
    );
    setUsage(map);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/service-categories/${confirmDelete.id}`);
      toast.success("Category removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8" data-testid="services-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyb-label">Configuration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Service Categories</h1>
          <p className="text-sm text-slate-500 mt-1">Tag leads and opportunities with services. Delete is blocked while a category is still in use.</p>
        </div>
        <button
          data-testid="new-category-btn"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> New Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="service-categories-grid">
        {items.map((c) => {
          const u = usage[c.id] || { lead_count: 0, opp_count: 0, total: 0 };
          const inUse = u.total > 0;
          return (
            <div key={c.id} className="eyb-card p-4" data-testid={`service-cat-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {c.active
                      ? <Pill className="bg-emerald-50 text-emerald-700 border border-emerald-200">Active</Pill>
                      : <Pill className="bg-slate-100 text-slate-600 border border-slate-200">Inactive</Pill>}
                    <Pill className={inUse ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-500 border border-slate-200"}>
                      {inUse ? `${u.total} in use` : "Unused"}
                    </Pill>
                  </div>
                  {inUse && (
                    <div className="text-[11px] text-slate-500 mt-1.5">
                      {u.lead_count} lead{u.lead_count === 1 ? "" : "s"} · {u.opp_count} opp{u.opp_count === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`edit-cat-${c.id}`}
                    onClick={() => setEditing(c)}
                    className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    title="Rename / toggle active"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`delete-cat-${c.id}`}
                    onClick={() => setConfirmDelete({ ...c, usage: u })}
                    disabled={inUse}
                    className={`p-2 rounded-md ${inUse ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:text-rose-600 hover:bg-rose-50"}`}
                    title={inUse ? "Cannot delete: still in use" : "Delete"}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(showNew || editing) && (
        <CatModal
          cat={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-cat">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Delete category?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? (
                confirmDelete.usage?.total > 0 ? (
                  <>This category is still tagged on {confirmDelete.usage.lead_count} lead(s) and {confirmDelete.usage.opp_count} opportunity(ies). Reassign or untag them first, then delete.</>
                ) : (
                  <>Permanently remove <span className="font-semibold">{confirmDelete.name}</span>? This cannot be undone.</>
                )
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cat-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={!!confirmDelete && confirmDelete.usage?.total > 0}
              data-testid="confirm-delete-cat-action"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CatModal({ cat, onClose, onSaved }) {
  const [name, setName] = useState(cat?.name || "");
  const [active, setActive] = useState(cat?.active ?? true);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (cat) await api.patch(`/service-categories/${cat.id}`, { name: name.trim(), active });
      else await api.post("/service-categories", { name: name.trim(), active });
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="cat-modal">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading text-lg font-semibold">{cat ? "Edit category" : "New category"}</h3>
          {cat && <p className="text-[11px] text-slate-500 mt-1">Renaming will update every lead & opportunity already tagged with this category.</p>}
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="eyb-label">Name</span>
            <input
              data-testid="cat-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              autoFocus
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="cat-active-checkbox"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            /> Active
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" data-testid="cat-cancel" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button type="submit" data-testid="cat-save" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
